import {
  ProductListing,
  PlpDesignItem,
  PlpFilter,
  PlpFilterItem,
  type PlpSeoData,
} from '../../domain/models/ProductListing';
import type { PlpFilterDTO, PlpResponseDTO, PlpListingResponseDTO } from '../dto/plp.dto';
import type { PlpFiltersResponseDTO } from '@/features/filters/infra/dto/filters.dto';
import type { SeoResult } from '@/shared/features/seo/domain/interfaces/SeoRepository';
import type {
  ListingPageResult,
  ListingFilter,
  ListingFilterItem,
  ListingDesignItem,
  ExploreLink,
  ListingSeo,
} from '../../domain/models/ProductListing';
export { mapFilterDtosToListingFilters, adaptFiltersResponseDto } from '@/features/filters/infra/mappers/filters.mapper';
import { mapFilterDtosToListingFilters, adaptFiltersResponseDto } from '@/features/filters/infra/mappers/filters.mapper';

export { plainToListingSeo, adaptFaqsFromPlain } from '@/shared/features/seo/infra/mappers/seo.mapper';
import { plainToListingSeo, adaptFaqsFromPlain } from '@/shared/features/seo/infra/mappers/seo.mapper';

/** Map raw design records (PLP API or recently-viewed designItem payloads) to listing card items. */
export function mapRawDesignRecordsToListingDesignItems(
  designItemsRaw: unknown[],
): ListingDesignItem[] {
  return mapDesignDtosToDomain(designItemsRaw as PlpResponseDTO['designItems']) as ListingDesignItem[];
}

function mapDesignDtosToDomain(designItemsRaw: PlpResponseDTO['designItems']): PlpDesignItem[] {
  return (designItemsRaw ?? []).map((d) => {
    const raw = d as unknown as Record<string, unknown>;
    const mediaItems = raw.mediaItems as { carouselSeq?: Array<{ type: string; urlMap: Record<string, string> }> } | undefined;
    const carouselSeq = mediaItems?.carouselSeq ?? [];
    const videoCarouselItem = carouselSeq.find((item) => item.type === 'video');
    const videoUrl = videoCarouselItem?.urlMap?.video_url ?? (typeof raw.videoUrl === 'string' && raw.videoUrl ? raw.videoUrl : undefined);
    const videoPosterUrl = videoCarouselItem?.urlMap?.poster_url ?? (typeof raw.videoPosterUrl === 'string' && raw.videoPosterUrl ? raw.videoPosterUrl : undefined);

    const videoIndex = carouselSeq.findIndex((item) => item.type === 'video');
    let hoverImageSource: { urlMap: Record<string, string> } | undefined;
    if (videoIndex >= 0) {
      hoverImageSource = carouselSeq.slice(videoIndex + 1).find((item) => item.type === 'image');
    } else {
      const imageItems = carouselSeq.filter((item) => item.type === 'image');
      hoverImageSource = imageItems[1];
    }
    const hoverImageUrl = hoverImageSource?.urlMap?.['828']
      ?? Object.values(hoverImageSource?.urlMap ?? {}).find((v) => typeof v === 'string' && v.startsWith('http'))
      ?? (typeof raw.hoverImageUrl === 'string' && raw.hoverImageUrl ? raw.hoverImageUrl : undefined);

    const firstImageItem = carouselSeq.find((item) => item.type === 'image');
    const analyticsImageUrl = firstImageItem?.urlMap?.['1024']
      ?? Object.values(firstImageItem?.urlMap ?? {}).find((v) => typeof v === 'string' && v.startsWith('http'))
      ?? (typeof raw.imageUrl === 'string' && raw.imageUrl ? raw.imageUrl : undefined);

    const categoryType = typeof raw.categoryType === 'string' && raw.categoryType ? raw.categoryType : undefined;
    const customizationOptionsMetal = (raw.customizationOptions as { metal?: Array<{ color?: string }> } | undefined)?.metal;
    const metalFromOptions = Array.isArray(customizationOptionsMetal) && customizationOptionsMetal.length > 0
      ? (typeof customizationOptionsMetal[0]?.color === 'string' ? customizationOptionsMetal[0].color : undefined)
      : undefined;
    const metalColor = metalFromOptions ?? (typeof raw.metal === 'string' && raw.metal ? raw.metal : undefined);

    return new PlpDesignItem({
      designId: Number(raw.designId ?? 0),
      designName: String(raw.designName ?? ''),
      imageUrl: String(raw.imageUrl ?? raw.imageURL ?? ''),
      productPageUrl: String(raw.productPageUrl ?? raw.productPageURL ?? ''),
      price: String(raw.price ?? ''),
      discountedPrice: raw.discountedPrice != null ? String(raw.discountedPrice) : undefined,
      discountPercent: raw.discountPercent != null ? Number(raw.discountPercent) : undefined,
      discountOnDiamondPriceValue:
        raw.discountOnDiamondPriceValue != null ? Number(raw.discountOnDiamondPriceValue) : undefined,
      discountName: typeof raw.discountName === 'string' && raw.discountName ? raw.discountName : undefined,
      discountValue: raw.discountValue != null ? String(raw.discountValue) : undefined,
      customizationId: raw.customizationId != null ? Number(raw.customizationId) : undefined,
      ...(videoUrl ? { videoUrl } : {}),
      ...(videoPosterUrl ? { videoPosterUrl } : {}),
      ...(hoverImageUrl ? { hoverImageUrl } : {}),
      ...(categoryType ? { categoryType } : {}),
      ...(metalColor ? { metal: metalColor } : {}),
      ...(analyticsImageUrl ? { analyticsImageUrl } : {}),
      sourceType: typeof raw.sourceType === 'string' ? raw.sourceType : undefined,
    });
  });
}

/**
 * Merge split API responses into a single ProductListing domain model.
 * The seo param is a pre-mapped SeoResult from SeoRepository.
 */
export function mergePlpSplitResponses(
  listing: PlpListingResponseDTO | Record<string, unknown>,
  filters?: PlpFiltersResponseDTO | Record<string, unknown>,
  listingSeo?: SeoResult
): ProductListing {
  const listingRecord = listing as Record<string, unknown>;
  const totalCount = Number(listingRecord.totalCount ?? 0);
  const designs = mapDesignDtosToDomain(listingRecord.designItems as PlpResponseDTO['designItems']);
  const redirectUrl =
    typeof listingRecord.redirectUrl === 'string'
      ? listingRecord.redirectUrl
      : typeof listingRecord.redirect_url === 'string'
        ? listingRecord.redirect_url
        : undefined;

  const filtersResult = filters ? adaptFiltersResponseDto(filters) : { filters: [] as ListingFilter[] };
  const header = listingSeo?.listingSeo?.header ?? '';

  return new ProductListing({
    header,
    totalCount,
    filters: filtersResult.filters.map(
      (f) =>
        new PlpFilter({
          category: f.category,
          items: f.items.map(
            (item) =>
              new PlpFilterItem({
                name: item.name,
                displayName: item.displayName,
                count: item.count,
                url: item.url,
                selected: item.selected,
              })
          ),
        })
    ),
    designs,
    ...(redirectUrl && { redirectUrl }),
    ...(filtersResult.filtersResetUrl && { filtersResetUrl: filtersResult.filtersResetUrl }),
    ...(listingSeo?.listingSeo && { listingSeo: listingSeo.listingSeo as PlpSeoData }),
    ...(listingSeo?.faqs && { faqs: listingSeo.faqs }),
  });
}

/**
 * Adapter: DTO/plain object -> Domain model
 */
export function adaptPlpResponseDtoToDomain(
  input: PlpResponseDTO | Record<string, unknown>
): ProductListing {
  const record = input as Record<string, unknown>;
  // Split-response merge path when only listing fields are present
  if (record.designItems != null && record.filters == null && record.header == null && record.listingSeo == null) {
    return mergePlpSplitResponses(record);
  }

  const header = String((input as PlpResponseDTO).header ?? '');
  const totalCount = Number((input as PlpResponseDTO).totalCount ?? 0);
  const filters = mapFilterDtosToListingFilters((input as PlpResponseDTO).filters ?? []);
  const designs = mapDesignDtosToDomain((input as PlpResponseDTO).designItems ?? []);

  const redirectUrl =
    typeof (input as PlpResponseDTO).redirectUrl === 'string'
      ? (input as PlpResponseDTO).redirectUrl
      : typeof record.redirect_url === 'string'
        ? record.redirect_url as string
        : undefined;
  const filtersResetUrl =
    typeof (input as PlpResponseDTO).filtersResetUrl === 'string'
      ? (input as PlpResponseDTO).filtersResetUrl
      : undefined;
  const listingSeo = plainToListingSeo(record);
  const faqs = adaptFaqsFromPlain(record);

  return new ProductListing({
    header,
    totalCount,
    filters: filters.map(
      (f) =>
        new PlpFilter({
          category: f.category,
          items: f.items.map(
            (item) =>
              new PlpFilterItem({
                name: item.name,
                displayName: item.displayName,
                count: item.count,
                url: item.url,
                selected: item.selected,
              })
          ),
        })
    ),
    designs,
    ...(redirectUrl && { redirectUrl }),
    ...(filtersResetUrl && { filtersResetUrl }),
    ...(listingSeo && { listingSeo: listingSeo as PlpSeoData }),
    ...(faqs && { faqs }),
  });
}

/**
 * Domain model -> plain object for Redux serialization
 */
export function productListingToPlainObject(listing: ProductListing): Record<string, unknown> {
  return {
    header: listing.header,
    totalCount: listing.totalCount,
    filters: listing.filters.map((f) => ({
      category: f.category,
      filterItemList: f.items.map((item) => ({
        name: item.name,
        displayName: item.displayName,
        count: item.count,
        url: item.url,
        selected: item.selected,
      })),
    })),
    designItems: listing.designs.map((d) => ({
      designId: d.designId,
      designName: d.designName,
      imageUrl: d.imageUrl,
      productPageUrl: d.productPageUrl,
      price: d.price,
      discountedPrice: d.discountedPrice,
      discountPercent: d.discountPercent,
      discountOnDiamondPriceValue: d.discountOnDiamondPriceValue,
      ...(d.discountName ? { discountName: d.discountName } : {}),
      ...(d.discountValue ? { discountValue: d.discountValue } : {}),
      ...(d.customizationId != null ? { customizationId: d.customizationId } : {}),
      ...(d.videoUrl ? { videoUrl: d.videoUrl } : {}),
      ...(d.videoPosterUrl ? { videoPosterUrl: d.videoPosterUrl } : {}),
      ...(d.hoverImageUrl ? { hoverImageUrl: d.hoverImageUrl } : {}),
      ...(d.categoryType ? { categoryType: d.categoryType } : {}),
      ...(d.metal ? { metal: d.metal } : {}),
      ...(d.analyticsImageUrl ? { analyticsImageUrl: d.analyticsImageUrl } : {}),
      ...(d.sourceType ? { sourceType: d.sourceType } : {}),
    })),
    ...(listing.redirectUrl && { redirect_url: listing.redirectUrl }),
    ...(listing.filtersResetUrl && { filtersResetUrl: listing.filtersResetUrl }),
    ...(listing.listingSeo && { listingSeo: listing.listingSeo }),
    ...(listing.faqs?.length && { faqs: listing.faqs }),
    ...(typeof listing.listingSeo?.prev === 'string' && listing.listingSeo.prev ? { prev: listing.listingSeo.prev as string } : {}),
    ...(typeof listing.listingSeo?.next === 'string' && listing.listingSeo.next ? { next: listing.listingSeo.next as string } : {}),
  };
}

/**
 * Convert getPlp transformResponse plain object to ListingPageResult.
 * Used by SSR and anywhere we need the presentation contract shape.
 */
export function plainPlpResponseToListingPageResult(
  plain: Record<string, unknown>
): ListingPageResult {
  const filters = ((plain.filters as Array<{ category: string; filterItemList?: Array<Record<string, unknown>> }>) ?? []).map(
    (f): ListingFilter => ({
      category: f.category,
      items: (f.filterItemList ?? []).map(
        (item): ListingFilterItem => ({
          name: String(item.name ?? ''),
          displayName: String(item.displayName ?? ''),
          count: Number(item.count ?? 0),
          url: String(item.url ?? ''),
          selected: Boolean(item.selected),
        })
      ),
    })
  );
  const designItems = (plain.designItems as Array<Record<string, unknown>>) ?? [];
  const designs: ListingDesignItem[] = designItems.map((d) => ({
    designId: Number(d.designId ?? 0),
    designName: String(d.designName ?? ''),
    imageUrl: String(d.imageUrl ?? d.imageURL ?? ''),
    productPageUrl: String(d.productPageUrl ?? d.productPageURL ?? ''),
    price: String(d.price ?? ''),
    discountedPrice: d.discountedPrice != null ? String(d.discountedPrice) : undefined,
    discountPercent: d.discountPercent != null ? Number(d.discountPercent) : undefined,
    discountOnDiamondPriceValue:
      d.discountOnDiamondPriceValue != null ? Number(d.discountOnDiamondPriceValue) : undefined,
    ...(d.discountName != null ? { discountName: String(d.discountName) } : {}),
    ...(d.discountValue != null ? { discountValue: String(d.discountValue) } : {}),
    ...(d.customizationId != null ? { customizationId: Number(d.customizationId) } : {}),
    ...(d.videoUrl != null ? { videoUrl: String(d.videoUrl) } : {}),
    ...(d.videoPosterUrl != null ? { videoPosterUrl: String(d.videoPosterUrl) } : {}),
    ...(d.hoverImageUrl != null ? { hoverImageUrl: String(d.hoverImageUrl) } : {}),
    ...(d.categoryType != null ? { categoryType: String(d.categoryType) } : {}),
    ...(d.metal != null ? { metal: String(d.metal) } : {}),
    ...(d.analyticsImageUrl != null ? { analyticsImageUrl: String(d.analyticsImageUrl) } : {}),
    ...(d.sourceType != null ? { sourceType: String(d.sourceType) } : {}),
  }));
  const listingSeo = (plain.listingSeo as ListingSeo | undefined) ?? plainToListingSeo(plain);
  const resolvedListingSeo: ListingSeo | undefined = listingSeo ? {
    ...listingSeo,
    prev: listingSeo.prev ?? (typeof plain.prev === 'string' ? plain.prev : undefined),
    next: listingSeo.next ?? (typeof plain.next === 'string' ? plain.next : undefined),
  } : undefined;
  const seoLinksFromApi =
    (plain.listingSeo as { seoLinks?: ExploreLink[] } | undefined)?.seoLinks
    ?? (plain.seoLinks as ExploreLink[] | undefined)
    ?? listingSeo?.seoLinks;
  const exploreLinks = seoLinksFromApi ?? (plain.exploreLinks as ExploreLink[] | undefined) ?? undefined;
  const headerImageUrl = typeof plain.headerImageUrl === 'string' ? plain.headerImageUrl : undefined;
  const redirectUrl = typeof plain.redirectUrl === 'string' ? plain.redirectUrl : typeof plain.redirect_url === 'string' ? plain.redirect_url : undefined;
  const filtersResetUrl = typeof plain.filtersResetUrl === 'string' ? plain.filtersResetUrl : undefined;
  const faqs = adaptFaqsFromPlain(plain);
  return {
    header: String(plain.header ?? ''),
    ...(headerImageUrl && { headerImageUrl }),
    totalCount: Number(plain.totalCount ?? 0),
    filters,
    designs,
    ...(filtersResetUrl && { filtersResetUrl }),
    ...(exploreLinks?.length && { exploreLinks }),
    ...(redirectUrl && { redirectUrl }),
    ...(resolvedListingSeo && { listingSeo: resolvedListingSeo }),
    ...(faqs && { faqs }),
  };
}

/**
 * @deprecated Use adaptFiltersResponseDto — kept for backward compatibility.
 */
export function adaptFilterPreviewDtoToListingFilters(data: PlpFilterDTO[]): ListingFilter[] {
  return mapFilterDtosToListingFilters(data);
}

